/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LanguageCard } from 'src/languageStudio/appShell/prebuiltDemos/components/LanguageCard';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { TranslatorLanguage } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { DetectedLanguage } from 'src/languageStudio/core/services/textAnalytics/textAnalyticsSwaggers';
import { TextTranslationSwagger } from 'src/languageStudio/core/services/translation/translatorSwagger';

const messages = defineMessages({
    translationTitle: { id: 'languageStudio.visualizeTextTranslationResults.translationTitle', defaultMessage: 'Translation' },
    transliterationTitle: { id: 'languageStudio.visualizeTextTranslationResults.transliterationTitle', defaultMessage: 'Transliteration' },
    sentencesLengthTitle: {
        id: 'languageStudio.visualizeTextTranslationResults.sentencesLengthTitle',
        defaultMessage: 'Calculate sentence length'
    }
});

type VisualizeTextTranslationResultsProps = {
    document: string;
    targetLanguage: string;
    results: TextTranslationSwagger;
    languagesList: TranslatorLanguage[];
};

export const VisualizedTextTranslationResults = (props: VisualizeTextTranslationResultsProps) => {
    const { results, document, targetLanguage, languagesList } = props;
    const { localize } = useLocalization();

    const translatedText = results.translations?.filter(t => t.to === targetLanguage)?.[0];
    const detectedLanguage: DetectedLanguage = results.detectedLanguage && {
        confidenceScore: results.detectedLanguage.score,
        iso6391Name: results.detectedLanguage.language as Language['code'],
        name: languagesList.find(l => l.code === results.detectedLanguage.language).name
    };

    return (
        <Stack tokens={{ childrenGap: 24 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText fontWeight={500}>{localize(messages.translationTitle)}</LuisText>
                <LuisText>{translatedText.text}</LuisText>
            </Stack>

            {translatedText.transliteration && (
                <Stack tokens={{ childrenGap: 8 }}>
                    <LuisText fontWeight={500}>{localize(messages.transliterationTitle)}</LuisText>
                    <LuisText>{translatedText.transliteration?.text}</LuisText>
                </Stack>
            )}

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText as="h3" fontWeight={500}>
                    {localize(commonDemosMessages.originalText)}
                </LuisText>
                <LuisText>{document}</LuisText>
            </Stack>

            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText fontWeight={500}>{localize(messages.sentencesLengthTitle)}</LuisText>
                <LuisText>{translatedText.sentLen.transSentLen.join(', ')}</LuisText>
            </Stack>

            <LanguageCard language={detectedLanguage} />
        </Stack>
    );
};
